/**
 * IdentityMatchPanel
 *
 * Shared live identity resolution: "does this physical person already exist
 * in the tenant?" — the same panel across guest check-in and staff
 * onboarding, so operators learn it once. Generalized from the shipped
 * GuestIdentityPanel (guests module) without visual changes to the cards.
 *
 * Contexts:
 * - `guest_checkin`  — front-desk recovery of a returning guest. Renders
 *   expanded (the shipped behavior). An active-staff candidate can be linked
 *   but not checked in (note in card; the block lands at check-in).
 * - `staff_onboarding` — HR dedup while typing the new employee's identity.
 *   Renders as a COLLAPSED one-row bar (best candidate + "+N more"), expanding
 *   on demand — it informs without eating the form. An active-staff candidate
 *   is a hard block here (manage from their profile); an in-house guest can be
 *   linked but the hire is rejected until check-out (backend rule).
 *
 * Identity is TENANT-GLOBAL (a Person has no location), so candidates can come
 * from other locations or from the other role domain. Candidates arrive capped
 * at 5 (strong first, recency-ranked); a bare common name past the overflow
 * threshold returns only a count, and the panel asks for a discriminating
 * field instead of listing identical cards.
 *
 * i18n note: strings shared with the original check-in panel keep their
 * `guests.checkin.match.*` keys (existing translations); strings introduced by
 * the generalization live under `identityMatch.*`.
 *
 * Dark-mode note: only NON-alpha palette utilities adapt to dark via
 * src/index.css — this component sticks to semantic tokens + non-alpha palette.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Search, Check, X, History, Nfc, Loader2, CalendarDays, Briefcase, ChevronDown, ChevronUp, UserCheck,
} from 'lucide-react';

import { useTranslation } from '@/core';
import { useAuth } from '@/core/auth/useAuth';
import { hasPermission } from '@/core/auth/permissions';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useScanBandOnce } from '@/shared/hooks/useScanBandOnce';
import { useFormattedDate } from '@/shared/hooks/useFormattedDate';
import { Button } from '@/theme/components/ui/button';
import { useCheckinMatch } from '@/entities/person/hooks/usePersons';
import type { CheckinMatchCandidate } from '@/entities/person/types';

export type IdentityMatchContext = 'guest_checkin' | 'staff_onboarding' | 'user_creation';

function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export interface IdentityMatchPanelProps {
  context: IdentityMatchContext;
  firstName?: string;
  lastName?: string;
  email?: string;
  birthDate?: string;
  documentNumber?: string;
  /** The flow's own person (e.g. the booking guest's) — never offered as a match. */
  currentPersonId?: string;
  onUseProfile: (candidate: CheckinMatchCandidate) => void;
  /**
   * Name of the person this flow has been linked to. When set, identity is
   * RESOLVED: the panel collapses to a confirmation and stops matching.
   */
  linkedPersonName?: string | null;
  onChangeLink?: () => void;
  /** Seed the list expanded on mount (wizards' "review matches" jump-back). */
  startExpanded?: boolean;
  /**
   * Reports how many matches are on the table (visible candidates, or the
   * overflow count) so the host can guard "create without linking".
   */
  onMatchesFound?: (count: number) => void;
}

// All dates render through useFormattedDate (location setup defines the
// format — European dd/mm/yyyy by default). Never raw ISO, history included.
type DateFormatter = (date: string | null | undefined, formatKey: string) => string;

function formatQuickData(c: CheckinMatchCandidate, dash: string, formatDate: DateFormatter): string {
  const p = c.person;
  const parts = [
    p.nationalityIso || null,
    p.birthDate ? `b. ${formatDate(p.birthDate, 'SHORT_DATE')}` : null,
    p.documentType ? p.documentType : null,
    p.email || null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : dash;
}

/** A candidate currently checked in as a guest (in-house) — blocks hiring. */
function isInhouseGuest(c: CheckinMatchCandidate): boolean {
  return c.history.recent.some((s) => s.status === 'checked_in');
}

function candidateInitials(c: CheckinMatchCandidate): string {
  return (
    (c.person.firstName?.[0] || c.person.name?.[0] || '?').toUpperCase()
    + (c.person.lastName?.[0] || '').toUpperCase()
  );
}

function candidateName(c: CheckinMatchCandidate): string {
  return c.person.name || `${c.person.firstName} ${c.person.lastName}`.trim();
}

/**
 * In-panel history for a candidate — tenant-wide, spanning guest stays and
 * the staff stint, so the operator verifies before linking.
 */
const CandidateHistory: React.FC<{ candidate: CheckinMatchCandidate }> = ({ candidate }) => {
  const { t } = useTranslation();
  const { format } = useFormattedDate();
  const { history } = candidate;
  const hasGuest = history.recent.length > 0;
  const hasStaff = !!history.staff;

  return (
    <div className="mt-3 space-y-3 rounded-[var(--radius)] border border-border bg-muted/40 p-3.5">
      {hasGuest ? (
        <div>
          <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
            <CalendarDays className="size-3.5 text-ocean-700" />
            {t('guests.checkin.match.history.stays', 'Stay history')}
          </div>
          <ul className="space-y-1.5">
            {history.recent.map((s, i) => (
              <li
                key={i}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-l-2 border-ocean-200 pl-3 text-[12px]"
              >
                <span className="font-mono font-semibold text-foreground">
                  {s.arrivalDate ? format(s.arrivalDate, 'SHORT_DATE') : '—'}
                  {s.departureDate ? ` → ${format(s.departureDate, 'SHORT_DATE')}` : ''}
                </span>
                <span className="text-muted-foreground">
                  {[s.room, s.bookingReference ? `#${s.bookingReference}` : null, s.status]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasStaff ? (
        <div className={hasGuest ? 'border-t border-border pt-3' : ''}>
          <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
            <Briefcase className="size-3.5 text-sage-700" />
            {t('guests.checkin.match.history.staff', 'Staff history')}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-l-2 border-sage-300 pl-3 text-[12px]">
            <span className="font-semibold text-foreground">
              {history.staff!.position || t('guests.checkin.match.history.staffRole', 'Staff')}
            </span>
            <span className="text-muted-foreground">
              {[
                history.staff!.location,
                history.staff!.hireDate
                  ? t('guests.checkin.match.history.since', 'since {{date}}', { date: format(history.staff!.hireDate, 'SHORT_DATE') })
                  : null,
                history.staff!.status,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </div>
      ) : null}

      {!hasGuest && !hasStaff ? (
        <p className="text-[12px] text-muted-foreground">
          {t('guests.checkin.match.history.empty', 'No stay or staff history on file yet.')}
        </p>
      ) : null}
    </div>
  );
};

export const IdentityMatchPanel: React.FC<IdentityMatchPanelProps> = ({
  context,
  firstName,
  lastName,
  email,
  birthDate,
  documentNumber,
  currentPersonId,
  onUseProfile,
  linkedPersonName,
  onChangeLink,
  startExpanded,
  onMatchesFound,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const { scan, scanning } = useScanBandOnce();
  const { format } = useFormattedDate();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set());
  const [scannedUid, setScannedUid] = useState<string | undefined>(undefined);
  // Staff onboarding starts collapsed — one row, expand on demand. Check-in
  // keeps the shipped always-expanded behavior.
  const [listOpen, setListOpen] = useState(startExpanded ?? context === 'guest_checkin');
  const resolved = !!linkedPersonName;

  const handleScan = async () => {
    const uid = await scan();
    if (uid) setScannedUid(uid);
  };

  const toggleHistory = (id: string) =>
    setExpandedHistory((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Client-side mirror of the backend gate (_has_any_location_permission,
  // edit=True): don't auto-fire a query the server would 403. Company admins
  // bypass the location gate; per-location the relevant edit permission
  // depends on the context's domain.
  const canMatch =
    hasPermission(user, 'company.full_access')
    || hasPermission(user, 'company.manage_users')
    || (context !== 'user_creation'
      && !!activeLocationId
      && (context === 'staff_onboarding'
        ? hasPermission(user, 'location.edit_staff', activeLocationId)
        : (hasPermission(user, 'location.edit_bookings', activeLocationId)
          || hasPermission(user, 'location.manage_guests', activeLocationId))));

  const input = useMemo(
    () => ({ firstName, lastName, email, birthDate, documentNumber, nfcUid: scannedUid }),
    [firstName, lastName, email, birthDate, documentNumber, scannedUid],
  );
  const debounced = useDebounced(input, 350);
  // Stop matching once identity is resolved — otherwise editing the pre-filled
  // fields re-fires the match and resurfaces the linked person + new candidates.
  const { data, isFetching } = useCheckinMatch(debounced, canMatch && !resolved);

  const candidates = (data?.candidates ?? []).filter(
    (c) => c.person.id !== currentPersonId && !dismissed.has(c.person.id),
  );
  const overflow = data?.overflow ?? null;
  // Only what the backend CAP hid counts as hidden — candidates we filter out
  // client-side (the flow's own person, dismissed cards) are resolved, not
  // hidden, and must not produce a misleading "Showing 0 of 1" footer.
  const backendReturned = data?.candidates.length ?? 0;
  const hiddenCount = Math.max(0, (data?.total ?? backendReturned) - backendReturned);
  const best = candidates[0] ?? null;

  const matchesOnTable = overflow ? overflow.count : candidates.length;
  useEffect(() => {
    onMatchesFound?.(resolved ? 0 : matchesOnTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesOnTable, resolved]);

  if (!canMatch) return null;

  // Resolved: identity linked — collapse to a confirmation so the operator
  // keeps working without the match list reappearing.
  if (resolved) {
    return (
      <section className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius)] border border-sage-300 bg-sage-50 px-5 py-3.5 shadow-sm">
        <span className="flex items-center gap-2 text-[13.5px] font-medium text-sage-800">
          <UserCheck className="size-4" />
          {t('guests.checkin.match.linkedTo', 'Identity linked to {{name}} — editing their profile', {
            name: linkedPersonName,
          })}
        </span>
        {onChangeLink ? (
          <Button type="button" size="sm" variant="ghost" className="h-8 px-3" onClick={onChangeLink}>
            <X className="size-3.5" />
            {t('guests.checkin.match.changeLink', 'Change')}
          </Button>
        ) : null}
      </section>
    );
  }

  const statusText = isFetching || scanning
    ? t('guests.checkin.match.searching', 'matching…')
    : overflow
      ? t('identityMatch.overflowCount', '{{count}} people match', { count: overflow.count })
      : candidates.length
        ? t('guests.checkin.match.count', '{{count}} possible match', { count: candidates.length })
        : t('guests.checkin.match.none', 'no match yet');

  // Collapsed one-row bar (staff onboarding default): best candidate + count.
  if (!listOpen) {
    return (
      <section className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-[var(--radius)] border border-coral-200 bg-coral-50 px-4 py-2.5 shadow-sm">
        <span className="flex items-center gap-2 font-serif text-[14.5px] font-semibold tracking-[-0.01em] text-foreground">
          <Search className="size-4 text-coral-700" />
          {t('identityMatch.title', 'Person identity')}
        </span>

        {overflow ? (
          <span className="min-w-0 flex-1 text-[12.5px] text-sun-800">
            {t(
              'identityMatch.overflowHint',
              '{{count}} people share this name — add a birth date, document or email to narrow down.',
              { count: overflow.count },
            )}
          </span>
        ) : best ? (
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <span
              className={`grid size-7 flex-none place-items-center rounded-full font-serif text-[11px] font-semibold ${
                best.confidence === 'strong' ? 'bg-sage-100 text-sage-800' : 'bg-sun-100 text-sun-800'
              }`}
            >
              {candidateInitials(best)}
            </span>
            <span className="truncate font-serif text-[14px] font-semibold text-foreground">
              {candidateName(best)}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                best.confidence === 'strong' ? 'bg-sage-100 text-sage-800' : 'bg-sun-100 text-sun-800'
              }`}
            >
              {best.confidence === 'strong'
                ? t('guests.checkin.match.strong', 'Strong match')
                : t('guests.checkin.match.possible', 'Possible')}
            </span>
            {best.history.staff ? (
              <span className={`rounded-full px-2 py-0.5 font-mono text-[10.5px] font-semibold ${
                best.history.staff.isActive ? 'bg-sun-100 text-sun-800' : 'bg-sage-100 text-sage-800'
              }`}
              >
                {best.history.staff.isActive
                  ? t('guests.checkin.match.activeStaff', '⚠ active staff')
                  : t('guests.checkin.match.wasStaff', 'was staff')}
              </span>
            ) : null}
            {candidates.length + hiddenCount > 1 ? (
              <span className="whitespace-nowrap text-[12px] text-muted-foreground">
                {t('identityMatch.more', '+{{count}} more possible', { count: candidates.length + hiddenCount - 1 })}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="min-w-0 flex-1 font-mono text-[11.5px] text-muted-foreground">{statusText}</span>
        )}

        <span className="flex items-center gap-1.5">
          <Button type="button" size="sm" variant="ghost" className="h-8 px-2.5" onClick={handleScan} disabled={scanning}>
            {scanning ? <Loader2 className="size-3.5 animate-spin" /> : <Nfc className="size-3.5" />}
          </Button>
          {(candidates.length > 0 || overflow) ? (
            <Button type="button" size="sm" variant="outline" className="h-8 px-3" onClick={() => setListOpen(true)}>
              {t('identityMatch.review', 'Review matches')}
              <ChevronDown className="size-3.5" />
            </Button>
          ) : null}
        </span>
      </section>
    );
  }

  return (
    <section className="rounded-[var(--radius)] border border-coral-200 bg-coral-50 px-5 py-[18px] shadow-sm">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-serif text-[17px] font-semibold tracking-[-0.01em] text-foreground">
          <Search className="size-4 text-coral-700" />
          {context === 'guest_checkin'
            ? t('guests.checkin.match.title', 'Guest identity')
            : t('identityMatch.title', 'Person identity')}
        </h3>
        <span className="flex items-center gap-2">
          <span className="font-mono text-[11.5px] text-muted-foreground">{statusText}</span>
          {context !== 'guest_checkin' ? (
            <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => setListOpen(false)}>
              <ChevronUp className="size-3.5" />
            </Button>
          ) : null}
        </span>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-3"
          onClick={handleScan}
          disabled={scanning}
        >
          {scanning ? <Loader2 className="size-3.5 animate-spin" /> : <Nfc className="size-3.5" />}
          {scanning
            ? t('guests.checkin.match.scanning', 'Tap band…')
            : t('guests.checkin.match.scan', 'Scan wristband')}
        </Button>
        <span className="text-[12px] text-muted-foreground">
          {t('identityMatch.scanHint', 'or matching on the details above')}
        </span>
      </div>

      {overflow ? (
        <div className="rounded-[var(--radius)] border border-border bg-muted/40 p-3.5">
          <p className="text-[13px] font-medium text-foreground">
            {t('identityMatch.overflowTitle', '{{count}} people share this name', { count: overflow.count })}
          </p>
          <p className="mt-1 text-[12px] text-muted-foreground">
            {t(
              'identityMatch.overflowBody',
              'A name alone can’t identify them. Add a birth date, document number or email — or scan their wristband.',
            )}
          </p>
        </div>
      ) : null}

      {candidates.length === 0 ? null : (
      <ul className="flex flex-col gap-2.5">
        {candidates.map((c) => {
          const strong = c.confidence === 'strong';
          const isOpen = expandedHistory.has(c.person.id);
          const activeStaff = !!c.history.staff?.isActive;
          const inhouse = context === 'staff_onboarding' && isInhouseGuest(c);
          // Hard blocks per context: onboarding staff for someone who IS
          // active staff, or creating a login for someone who HAS one —
          // in both cases the thing being created already exists.
          const blocked = (context === 'staff_onboarding' && activeStaff)
            || (context === 'user_creation' && !!c.person.hasUser);
          return (
            <li
              key={c.person.id}
              className={`flex gap-3 rounded-[var(--radius)] border px-3.5 py-3 ${
                strong ? 'border-sage-300 bg-sage-50' : 'border-sun-300 bg-sun-50'
              }`}
            >
              <div
                className={`grid size-11 flex-none place-items-center rounded-full font-serif text-[15px] font-semibold ${
                  strong ? 'bg-sage-100 text-sage-800' : 'bg-sun-100 text-sun-800'
                }`}
              >
                {candidateInitials(c)}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-serif text-[15px] font-semibold text-foreground">
                    {candidateName(c)}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                      strong ? 'bg-sage-100 text-sage-800' : 'bg-sun-100 text-sun-800'
                    }`}
                  >
                    {strong
                      ? t('guests.checkin.match.strong', 'Strong match')
                      : t('guests.checkin.match.possible', 'Possible')}
                  </span>
                  <span className="rounded-full bg-coral-100 px-2 py-0.5 text-[10.5px] font-semibold text-coral-700">
                    {t('guests.checkin.match.returning', '↩ returning')}
                  </span>
                </div>

                <div className="mt-1 truncate font-mono text-[11px] text-muted-foreground">
                  {formatQuickData(c, t('guests.checkin.match.noDetails', 'no details on file'), format)}
                </div>

                {(c.history.pastStays > 0 || c.history.staff || c.person.hasUser) ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
                    <History className="size-3.5" />
                    {c.history.pastStays > 0 ? (
                      <span className="rounded-full bg-ocean-100 px-2 py-0.5 font-mono text-[10.5px] font-semibold text-ocean-700">
                        {t('guests.checkin.match.stays', '{{count}} past stays', { count: c.history.pastStays })}
                      </span>
                    ) : null}
                    {activeStaff ? (
                      <span className="rounded-full bg-sun-100 px-2 py-0.5 font-mono text-[10.5px] font-semibold text-sun-800">
                        {t('guests.checkin.match.activeStaff', '⚠ active staff')}
                      </span>
                    ) : c.history.staff ? (
                      <span className="rounded-full bg-sage-100 px-2 py-0.5 font-mono text-[10.5px] font-semibold text-sage-800">
                        {t('guests.checkin.match.wasStaff', 'was staff')}
                      </span>
                    ) : null}
                    {inhouse ? (
                      <span className="rounded-full bg-sun-100 px-2 py-0.5 font-mono text-[10.5px] font-semibold text-sun-800">
                        {t('identityMatch.inhouseGuest', '⚠ checked-in guest')}
                      </span>
                    ) : null}
                    {c.person.hasUser ? (
                      <span className="rounded-full bg-ocean-100 px-2 py-0.5 font-mono text-[10.5px] font-semibold text-ocean-700">
                        {t('identityMatch.hasAccount', 'has account')}
                      </span>
                    ) : null}
                    {c.history.lastStayDate ? (
                      <span>
                        {t('guests.checkin.match.last', 'last {{date}}', { date: format(c.history.lastStayDate, 'SHORT_DATE') })}
                        {c.history.lastRoom ? ` · ${c.history.lastRoom}` : ''}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {context === 'guest_checkin' && activeStaff ? (
                  <p className="mt-2 text-[11.5px] text-sun-800">
                    {t(
                      'guests.checkin.match.activeStaffNote',
                      'Linking is fine, but this person can’t be checked in as a guest until their staff record is deactivated.',
                    )}
                  </p>
                ) : null}
                {blocked ? (
                  <p className="mt-2 text-[11.5px] text-sun-800">
                    {context === 'user_creation'
                      ? t(
                        'identityMatch.hasAccountBlockNote',
                        'Already has a login — a second account can’t be created for the same person. Manage their roles from the users list instead.',
                      )
                      : t(
                        'identityMatch.activeStaffBlockNote',
                        'Already an active employee — a second staff record can’t be created. Manage their employment from the staff profile instead.',
                      )}
                  </p>
                ) : null}
                {inhouse ? (
                  <p className="mt-2 text-[11.5px] text-sun-800">
                    {t(
                      'identityMatch.inhouseGuestNote',
                      'Linking is fine, but this person is currently checked in as a guest — they can’t be hired until their stay is checked out.',
                    )}
                  </p>
                ) : null}

                <div className="mt-2.5 flex flex-wrap gap-2">
                  {!blocked ? (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-coral-500 px-3 text-white hover:bg-coral-600"
                      onClick={() => onUseProfile(c)}
                    >
                      <Check className="size-3.5" />
                      {t('guests.checkin.match.use', 'This is them — use profile')}
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-3"
                    aria-expanded={isOpen}
                    onClick={() => toggleHistory(c.person.id)}
                  >
                    <History className="size-3.5" />
                    {isOpen
                      ? t('guests.checkin.match.hideHistory', 'Hide history')
                      : t('guests.checkin.match.viewHistory', 'View history')}
                    <ChevronDown className={`size-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 px-3"
                    onClick={() => setDismissed((prev) => new Set(prev).add(c.person.id))}
                  >
                    <X className="size-3.5" />
                    {t('guests.checkin.match.dismiss', 'Not a match')}
                  </Button>
                </div>

                {isOpen ? <CandidateHistory candidate={c} /> : null}
              </div>
            </li>
          );
        })}
      </ul>
      )}

      {hiddenCount > 0 ? (
        <p className="mt-2.5 text-[12px] text-muted-foreground">
          {t(
            'identityMatch.showingOf',
            'Showing {{shown}} of {{total}} — add a birth date, document or email to narrow down.',
            { shown: candidates.length, total: candidates.length + hiddenCount },
          )}
        </p>
      ) : null}
    </section>
  );
};
